import { OverlayHandlerBuilder } from './types';
import { isMobileOrTablet } from './device';
import { clamp } from './utils';

type OverlayState = 'loading' | 'open' | 'closed';
type OverlayEvent = 'success' | 'cancel';
type OverlayCallback = (close: () => void, redirectUrl?: string) => void;

const dialogElementId = 'vipps-overlay-dialog';
const stylesElementId = 'vipps-overlay-styles';
const parentCloseButtonClass = 'vipps-overlay-parent-close';
const parentMessageSource = 'vipps-widget-sdk-parent';
const requestCancelEvent = 'request-cancel';

const injectStyles = (): void => {
  if (document.querySelector('#' + stylesElementId)) return;

  const style = document.createElement('style');
  style.id = stylesElementId;
  style.textContent = `
    #${dialogElementId}::backdrop {
      background-color: rgba(0, 0, 0, 0.5);
    }
    #${dialogElementId} {
      position: fixed;
      border: none;
      border-radius: 16px;
      padding: 0;
      overflow: hidden;
      box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
      width: 90vw;
      max-width: 480px;
      height: 560px; /* initial loader screen height */
      max-height: 90vh;
      min-width: 320px;
      background: #fff;
    }

    #${dialogElementId} .${parentCloseButtonClass} {
      position: absolute;
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
      border: none;
      border-radius: 50%;
      background-color: #f0f0f0;
      color: #666;
      display: inline-flex;
      vertical-align: middle;
      justify-content: center;
      align-items: center;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
      z-index: 2;
    }

    #${dialogElementId} .${parentCloseButtonClass}:hover {
      background-color: #e0e0e0;
    }

    #${dialogElementId} iframe {
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 16px;
      display: block;
    }
    @media (prefers-reduced-motion: no-preference) {
      #${dialogElementId} {
        transition: width 0.2s ease, height 0.2s ease;
      }
    }
  `;
  document.head.append(style);
};

export const overlay = (fn: () => Promise<string> | string): OverlayHandlerBuilder => {
  let state: OverlayState = 'closed';
  let dialogEl: HTMLDialogElement | null = null;
  let iframeEl: HTMLIFrameElement | null = null;
  let expectedOrigin: string | null = null;
  let isCancelInFlight = false;
  const callbacks: Partial<Record<OverlayEvent, OverlayCallback>> = {};
  const resolver = fn;

  const applyResize = (height: number) => {
    if (!dialogEl) return;

    const maxHeight = window.innerHeight * 0.9;

    const clampedHeight = clamp(32, height, maxHeight);

    dialogEl.style.height = `${clampedHeight}px`;
  };

  const handleMessage = (event: MessageEvent) => {
    if (expectedOrigin && event.origin !== expectedOrigin) return;
    if (iframeEl && event.source !== iframeEl.contentWindow) return;
    if (event.data?.source !== 'vipps-widget-sdk') return;

    if (event.data.event === 'resize') {
      applyResize(event.data.height);
      return;
    }

    const eventMap: Record<string, OverlayEvent> = { complete: 'success', cancel: 'cancel' };
    const mapped = eventMap[event.data.event];
    if (mapped === 'cancel') {
      if (isCancelInFlight) return;
      isCancelInFlight = true;

      callbacks.cancel?.(close, event.data.redirectUrl);
      return;
    }
    if (mapped) {
      callbacks[mapped]?.(close, event.data.redirectUrl);
    }
  };

  const startListening = () => window.addEventListener('message', handleMessage);
  const stopListening = () => window.removeEventListener('message', handleMessage);

  const createOrGetDialog = (): HTMLDialogElement => {
    injectStyles();

    dialogEl ??= document.querySelector<HTMLDialogElement>('dialog#' + dialogElementId);
    if (dialogEl) return dialogEl;

    const d = document.createElement('dialog');
    d.id = dialogElementId;

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = parentCloseButtonClass;
    closeButton.setAttribute('aria-label', 'Close payment window');
    closeButton.textContent = '×';

    const i = document.createElement('iframe');

    closeButton.addEventListener('click', () => {
      if (isCancelInFlight) return;
      isCancelInFlight = true;

      if (iframeEl?.contentWindow) {
        iframeEl.contentWindow.postMessage(
          {
            source: parentMessageSource,
            event: requestCancelEvent,
          },
          expectedOrigin ?? '*'
        );
      }

      callbacks.cancel?.(close, undefined);
    });

    d.append(closeButton);
    d.append(i);
    document.body.append(d);

    d.addEventListener('cancel', (e) => {
      e.preventDefault();
    });

    d.addEventListener('click', (e) => {
      if (e.target === d) {
        e.preventDefault();
        e.stopPropagation();
      }
    });

    d.addEventListener('close', () => {
      state = 'closed';
      stopListening();
      iframeEl = null;
      expectedOrigin = null;
    });

    dialogEl = d;
    return d;
  };

  const showModalWithUrl = (urlString: string) => {
    const d = createOrGetDialog();
    const iframe = d.querySelector('iframe');

    if (!iframe) return;
    const url = new URL(urlString);
    url.searchParams.set('vipps-widget', 'true');

    iframeEl = iframe;
    expectedOrigin = url.origin;

    iframe.src = url.toString();

    d.showModal();
    state = 'open';
    startListening();
  };

  const openWithUrl = (url: string) => {
    if (isMobileOrTablet()) {
      if (window.top) {
        window.top.location.assign(url);
      } else {
        window.location.assign(url);
      }
    } else {
      showModalWithUrl(url);
    }
  };

  const open = async (): Promise<void> => {
    isCancelInFlight = false;
    try {
      const url = await resolver();
      openWithUrl(url);
    } catch (error) {
      // Keep logging for troubleshooting while allowing callers to observe rejection.
      console.error('vipps-overlay: Failed to resolve overlay URL', error);
      throw error;
    }
  };

  const close = () => {
    const d = createOrGetDialog();
    d.close();
  };

  return {
    open,
    close,
    on(event: OverlayEvent, cb: OverlayCallback) {
      callbacks[event] = cb;
      return this;
    },
    get isOpen() {
      return state === 'open';
    },
  };
};
